import {
  array,
  InferOutput,
  literal,
  number,
  object,
  optional,
  pipe,
  safeParse,
  string,
  transform,
  ValiError,
} from 'valibot';

import {
  AlbumItemSchema,
  ArtistItemSchema,
  ArtistViewSchema,
  ArtWorkAttrSchema,
  PlaylistItemSchema,
  SongItemSchema,
} from '../apiSchema.ts';
import { taken } from '../utils.ts';

const DEFAULT_MAX_ARTIST_MEDIA_ITEMS = Number(
  import.meta.env.VITE_DEFAULT_MAX_ARTIST_MEDIA_ITEMS ?? 4,
);

function hasData<T>(data: T[]): boolean {
  return data.length > 0;
}

type ViewData<T> = {
  data: T[];
  next?: string;
  href?: string;
};

function toViewData<T extends ViewData<object>, V>(
  views: Record<string, T>,
  viewName: string,
  responseViewName: string,
  max: number,
): V | undefined {
  const viewData = views[viewName];
  if (!viewData || !hasData(viewData.data)) {
    return undefined;
  }

  const rawLen = viewData.data.length;
  return {
    [responseViewName]: taken(viewData.data, max),
    next: rawLen > max ? max - 1 : undefined,
    total: rawLen,
  } as V;
}

export const CatalogArtistsIdRequestArgsSchema = object({
  id: string(),
  limit: optional(number()),
});

export type ArtistMediaPageRequestArgs = InferOutput<
  typeof CatalogArtistsIdRequestArgsSchema
>;

const viewDataMap: Record<string, string> = {
  'appears-on-albums': 'appearsOnAlbums',
  'compilation-albums': 'compilationAlbums',
  'featured-albums': 'featuredAlbums',
  'featured-playlists': 'featuredPlaylists',
  'full-albums': 'fullAlbums',
  'latest-release': 'latestRelease',
  'live-albums': 'liveAlbums',
  'similar-artists': 'similarArtists',
  singles: 'singles',
  'top-songs': 'topSongs',
};

export const ArtistMediaPageResponseSchema = pipe(
  object({
    id: string(),
    type: literal('artists'),
    attributes: object({
      name: string(),
      artwork: optional(ArtWorkAttrSchema),
    }),
    meta: object({
      views: object({
        order: array(ArtistViewSchema),
      }),
    }),
    views: object({
      'live-albums': object({
        attributes: object({
          title: string(),
        }),
        next: optional(string()),
        data: array(AlbumItemSchema),
      }),
      'featured-albums': object({
        attributes: object({
          title: string(),
        }),
        next: optional(string()),
        data: array(AlbumItemSchema),
      }),
      'latest-release': object({
        attributes: object({
          title: string(),
        }),
        next: optional(string()),
        data: array(AlbumItemSchema),
      }),
      'top-songs': object({
        attributes: object({
          title: string(),
        }),
        next: optional(string()),
        data: array(SongItemSchema),
      }),
      'similar-artists': object({
        attributes: object({
          title: string(),
        }),
        next: optional(string()),
        data: array(ArtistItemSchema),
      }),
      'appears-on-albums': object({
        attributes: object({
          title: string(),
        }),
        next: optional(string()),
        data: array(AlbumItemSchema),
      }),
      'featured-playlists': object({
        attributes: object({
          title: string(),
        }),
        next: optional(string()),
        data: array(PlaylistItemSchema),
      }),
      'compilation-albums': object({
        attributes: object({
          title: string(),
        }),
        next: optional(string()),
        data: array(AlbumItemSchema),
      }),
      singles: object({
        attributes: object({
          title: string(),
        }),
        next: optional(string()),
        data: array(AlbumItemSchema),
      }),
      'full-albums': object({
        attributes: object({
          title: string(),
        }),
        next: optional(string()),
        data: array(AlbumItemSchema),
      }),
    }),
  }),
  transform(({ id, attributes, meta, views }) => {
    const { name, artwork } = attributes;
    const hiddenViews: string[] = [];
    const liveAlbums = toViewData(views, 'live-albums', 'albums', 5);
    if (!liveAlbums) hiddenViews.push('live-albums');
    const featuredAlbums = toViewData(
      views,
      'featured-albums',
      'albums',
      DEFAULT_MAX_ARTIST_MEDIA_ITEMS,
    );
    if (!featuredAlbums) hiddenViews.push('featured-albums');
    const latestRelease = toViewData(
      views,
      'latest-release',
      'albums',
      1,
    );
    if (!latestRelease) hiddenViews.push('latest-release');
    const topSongs = toViewData(views, 'top-songs', 'tracks', 5);
    if (!topSongs) hiddenViews.push('top-songs');
    const similarArtists = toViewData(
      views,
      'similar-artists',
      'artists',
      DEFAULT_MAX_ARTIST_MEDIA_ITEMS,
    );
    if (!similarArtists) hiddenViews.push('similar-artists');
    const appearsOnAlbums = toViewData(
      views,
      'appears-on-albums',
      'albums',
      DEFAULT_MAX_ARTIST_MEDIA_ITEMS,
    );
    if (!appearsOnAlbums) hiddenViews.push('appears-on-albums');
    const featuredPlaylists = toViewData(
      views,
      'featured-playlists',
      'playlists',
      DEFAULT_MAX_ARTIST_MEDIA_ITEMS,
    );
    if (!featuredPlaylists) hiddenViews.push('featured-playlists');
    const compilationAlbums = toViewData(
      views,
      'compilation-albums',
      'albums',
      DEFAULT_MAX_ARTIST_MEDIA_ITEMS,
    );
    if (!compilationAlbums) hiddenViews.push('compilation-albums');
    const singles = toViewData(
      views,
      'singles',
      'albums',
      DEFAULT_MAX_ARTIST_MEDIA_ITEMS,
    );
    if (!singles) hiddenViews.push('singles');
    const fullAlbums = toViewData(
      views,
      'full-albums',
      'albums',
      DEFAULT_MAX_ARTIST_MEDIA_ITEMS,
    );
    if (!fullAlbums) hiddenViews.push('full-albums');

    return {
      order: meta.views.order.filter((order: string) =>
        !hiddenViews.includes(order)
      ).map((order: string) => viewDataMap[order]),
      artist: {
        id,
        type: 'artists',
        name,
        artwork,
      },
      appearsOnAlbums,
      compilationAlbums,
      featuredAlbums,
      featuredPlaylists,
      fullAlbums,
      latestRelease,
      liveAlbums,
      similarArtists,
      singles,
      topSongs,
    };
  }),
);

export type ArtistMediaPageResponse = InferOutput<
  typeof ArtistMediaPageResponseSchema
>;

export const CatalogArtistsIdAPISchema = pipe(
  object({
    data: object({
      data: array(ArtistMediaPageResponseSchema),
    }),
  }),
);

const views = [
  'latest-release',
  'top-songs',
  'full-albums',
  'featured-albums',
  'featured-playlists',
  'singles',
  'live-albums',
  'compilation-albums',
  'appears-on-albums',
  'similar-artists',
];

export const $getArtistMediaPageData = async (
  player: MusicKit.MusicKitInstance,
  args: ArtistMediaPageRequestArgs,
): Promise<ArtistMediaPageResponse> => {
  const parsedArgs = safeParse(
    CatalogArtistsIdRequestArgsSchema,
    args,
  );
  if (!parsedArgs.success) {
    throw new ValiError(parsedArgs.issues);
  }

  const { id, limit } = parsedArgs.output;

  if (limit !== undefined && limit !== DEFAULT_MAX_ARTIST_MEDIA_ITEMS) {
    // NOTE: 実装の都合上、limitはVITE_DEFAULT_MAX_ARTIST_MEDIA_ITEMS（Web側のENV）で固定されています。
    // limitを変更したい場合は、Web側のENVを変更してください。
    throw new Error(
      `[getArtistMedia] Limit must be ${DEFAULT_MAX_ARTIST_MEDIA_ITEMS}, but got ${limit}`,
    );
  }

  const response = await player.api.music(
    `v1/catalog/${player.storefrontId}/artists/${id}`,
    {
      views,
    },
  );

  const parsedResponse = safeParse(
    CatalogArtistsIdAPISchema,
    response,
  );
  if (!parsedResponse.success) {
    throw new ValiError(parsedResponse.issues);
  }
  const { data } = parsedResponse.output;
  const artistData = data.data[0];
  if (!artistData) {
    throw new Error('Artist media data not found');
  }
  return artistData;
};
